/**
 * KRBN Garage
 * Theme-native module. No external dependencies, no network calls.
 *
 * Responsibilities
 * - Persist up to 3 vehicles in localStorage (key: krbn_garage)
 * - Drive the garage drawer (list view + make/family/model picker)
 * - Inject nominative fitment badges on every compatible product card
 * - Power the collection bar (summary, compatible-only filter, clear)
 * - Keep the header count in sync
 *
 * Data source: JSON emitted by snippets/garage-data.liquid
 * Product fitments: data-krbn-fitments="handle1,handle2" on <product-card>
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'krbn_garage';
  var MAX_VEHICLES = 3;
  var EVT = 'krbn:garage:change';

  /* ------------------------------------------------------------------ */
  /* Catalog                                                             */
  /* ------------------------------------------------------------------ */

  var catalog = [];
  var byHandle = {};

  function loadCatalog() {
    var node = document.querySelector('[data-krbn-garage-data]');
    if (!node) return;
    try {
      catalog = JSON.parse(node.textContent).filter(function (v) {
        return v && v.active !== false;
      });
    } catch (e) {
      catalog = [];
    }
    catalog.forEach(function (v) { byHandle[v.handle] = v; });
  }

  function yearsLabel(v) {
    if (!v.startYear) return '';
    return v.endYear ? v.startYear + '\u2013' + v.endYear : v.startYear + '+';
  }

  /* ------------------------------------------------------------------ */
  /* Storage                                                             */
  /* ------------------------------------------------------------------ */

  function readGarage() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var data = JSON.parse(raw);
      var list = Array.isArray(data.vehicles) ? data.vehicles : [];
      return list.filter(function (h) { return byHandle[h]; }).slice(0, MAX_VEHICLES);
    } catch (e) {
      return [];
    }
  }

  function writeGarage(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ vehicles: handles.slice(0, MAX_VEHICLES) }));
    } catch (e) { /* private mode: session-only behavior, no crash */ }
    document.dispatchEvent(new CustomEvent(EVT, { detail: { vehicles: handles } }));
  }

  function addVehicle(handle) {
    var garage = readGarage();
    if (garage.indexOf(handle) !== -1 || garage.length >= MAX_VEHICLES) return;
    garage.push(handle);
    writeGarage(garage);
  }

  function removeVehicle(handle) {
    writeGarage(readGarage().filter(function (h) { return h !== handle; }));
  }

  function clearGarage() {
    writeGarage([]);
  }

  /* ------------------------------------------------------------------ */
  /* Header count                                                        */
  /* ------------------------------------------------------------------ */

  function renderHeaderCount() {
    var garage = readGarage();
    document.querySelectorAll('[data-krbn-garage-count]').forEach(function (el) {
      el.textContent = garage.length;
      el.hidden = garage.length === 0;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Drawer                                                              */
  /* ------------------------------------------------------------------ */

  var picker = { step: 'make', make: null, family: null };

  function drawerEl() { return document.getElementById('garage-drawer'); }

  function openDrawer() {
    var d = drawerEl();
    if (!d) return;
    if (typeof d.show === 'function') d.show();
    else if (typeof d.open === 'function') d.open();
    else d.setAttribute('open', '');
  }

  function showView(name) {
    var root = document.querySelector('[data-krbn-garage-root]');
    if (!root) return;
    root.querySelectorAll('[data-krbn-view]').forEach(function (view) {
      view.hidden = view.getAttribute('data-krbn-view') !== name;
    });
  }

  function vehicleCard(v, actionAttr, actionLabel) {
    var img = v.image
      ? '<img class="krbn-vehicle-card__image" src="' + v.image + '" alt="" loading="lazy">'
      : '<span class="krbn-vehicle-card__image krbn-vehicle-card__image--empty" aria-hidden="true"></span>';
    var years = yearsLabel(v);
    var action;
    if (actionAttr === 'data-krbn-remove' || actionAttr === 'data-krbn-add') {
      var isRemove = actionAttr === 'data-krbn-remove';
      action =
        '<button type="button" class="' + (isRemove ? 'krbn-vehicle-card__remove' : 'krbn-vehicle-card__add') + '" ' + actionAttr + '="' + v.handle + '">' +
          '<span class="sr-only">' + (isRemove ? 'Remove ' : 'Add ') + v.name + '</span>' +
          '<svg viewBox="0 0 10 10" width="10" height="10" fill="none" aria-hidden="true" focusable="false">' +
            '<path d="' + (isRemove ? 'M1.5 1.5l7 7M8.5 1.5l-7 7' : 'M5 1v8M1 5h8') + '" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
          '</svg>' +
        '</button>';
    } else {
      action = '<button type="button" class="link krbn-vehicle-card__action" ' + actionAttr + '="' + v.handle + '">' + actionLabel + '</button>';
    }
    return (
      '<article class="krbn-vehicle-card" data-handle="' + v.handle + '">' +
        img +
        '<div class="krbn-vehicle-card__info">' +
          '<span class="krbn-vehicle-card__name">' + v.name + '</span>' +
          '<span class="krbn-vehicle-card__meta text-subdued">Chassis ' + v.chassis + (years ? ' \u00b7 ' + years : '') + '</span>' +
        '</div>' +
        action +
      '</article>'
    );
  }

  function renderGarageView() {
    var garage = readGarage();
    var list = document.querySelector('[data-krbn-garage-list]');
    var empty = document.querySelector('[data-krbn-garage-empty]');
    var slots = document.querySelector('[data-krbn-garage-slots]');
    var addBtn = document.querySelector('[data-krbn-action="open-picker"]');
    var clearBtn = document.querySelector('.krbn-garage__view [data-krbn-action="clear-garage"]');
    if (!list) return;

    list.innerHTML = garage.map(function (h) {
      return vehicleCard(byHandle[h], 'data-krbn-remove', 'Remove');
    }).join('');

    if (empty) empty.hidden = garage.length > 0;
    if (slots) {
      slots.textContent = garage.length + '/' + MAX_VEHICLES;
      slots.hidden = garage.length === 0;
    }
    if (addBtn) {
      addBtn.disabled = garage.length >= MAX_VEHICLES;
      addBtn.textContent = garage.length >= MAX_VEHICLES ? 'GARAGE FULL' : 'ADD VEHICLE';
    }
    if (clearBtn) clearBtn.hidden = garage.length === 0;
  }

  function uniqueSorted(items) {
    return items.filter(function (x, i) { return items.indexOf(x) === i; }).sort();
  }

  function renderPicker() {
    var grid = document.querySelector('[data-krbn-picker-grid]');
    var crumb = document.querySelector('[data-krbn-picker-breadcrumb]');
    if (!grid) return;

    var garage = readGarage();
    var html = '';
    var crumbText = '';

    if (picker.step === 'make') {
      crumbText = 'Select a make';
      var makes = uniqueSorted(catalog.map(function (v) { return v.make; }));
      html = makes.map(function (make) {
        return '<button type="button" class="krbn-picker-tile" data-krbn-pick-make="' + make + '">' + make + '</button>';
      }).join('');
    } else if (picker.step === 'family') {
      crumbText = picker.make + ' \u00b7 select a chassis';
      var families = uniqueSorted(catalog
        .filter(function (v) { return v.make === picker.make; })
        .map(function (v) { return v.family; }));
      html = families.map(function (family) {
        return '<button type="button" class="krbn-picker-tile" data-krbn-pick-family="' + family + '">' + family + '</button>';
      }).join('');
    } else {
      crumbText = picker.make + ' \u00b7 ' + picker.family + ' \u00b7 select your model';
      var models = catalog.filter(function (v) {
        return v.make === picker.make && v.family === picker.family;
      });
      html = models.map(function (v) {
        if (garage.indexOf(v.handle) !== -1) {
          return vehicleCard(v, 'data-krbn-noop', 'In garage');
        }
        return vehicleCard(v, 'data-krbn-add', 'Add');
      }).join('');
    }

    grid.innerHTML = html;
    grid.classList.toggle('krbn-garage__picker-grid--models', picker.step === 'model');
    if (crumb) crumb.textContent = crumbText;
  }

  function openPicker() {
    picker = { step: 'make', make: null, family: null };
    renderPicker();
    showView('picker');
  }

  function pickerBack() {
    if (picker.step === 'model') {
      // Skip the family step on the way back when the make has one family
      var families = uniqueSorted(catalog
        .filter(function (v) { return v.make === picker.make; })
        .map(function (v) { return v.family; }));
      picker.step = families.length > 1 ? 'family' : 'make';
    } else if (picker.step === 'family') {
      picker.step = 'make';
    } else {
      showView('garage');
      return;
    }
    renderPicker();
  }

  /* ------------------------------------------------------------------ */
  /* Fitment badges                                                      */
  /* ------------------------------------------------------------------ */

  function fitmentsOf(card) {
    var raw = card.getAttribute('data-krbn-fitments') || '';
    return raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function processCard(card) {
    var garage = readGarage();
    var fitments = fitmentsOf(card);
    var matches = fitments.filter(function (h) { return garage.indexOf(h) !== -1; });

    var holder = card.querySelector('[data-krbn-fitment-badges]');
    if (!holder) {
      holder = document.createElement('div');
      holder.setAttribute('data-krbn-fitment-badges', '');
      holder.className = 'krbn-fitment-badges';
      card.appendChild(holder);
    }

    holder.innerHTML = matches.map(function (h) {
      return '<span class="badge krbn-badge">Fits ' + byHandle[h].name + '</span>';
    }).join('');
    holder.hidden = matches.length === 0;

    card.classList.toggle('krbn-compatible', matches.length > 0);
    var row = card.closest('li') || card;
    row.toggleAttribute('data-krbn-filtered-out', filterActive && matches.length === 0);
  }

  function processAllCards(scope) {
    (scope || document).querySelectorAll('[data-krbn-fitments]').forEach(processCard);
    renderFilterCount();
  }

  function observeNewCards() {
    var observer = new MutationObserver(function (mutations) {
      var found = false;
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.hasAttribute && node.hasAttribute('data-krbn-fitments')) {
            processCard(node);
            found = true;
          } else if (node.querySelector && node.querySelector('[data-krbn-fitments]')) {
            processAllCards(node);
            found = true;
          }
        });
      });
      if (found) renderFilterCount();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ */
  /* Collection bar and filter                                           */
  /* ------------------------------------------------------------------ */

  var filterActive = false;

  function renderCollectionBar() {
    var bar = document.querySelector('[data-krbn-collection-bar]');
    if (!bar) return;
    var garage = readGarage();
    bar.hidden = garage.length === 0;
    if (garage.length === 0 && filterActive) setFilter(false);

    var names = bar.querySelector('[data-krbn-bar-vehicles]');
    if (names) {
      names.textContent = garage.map(function (h) { return byHandle[h].name; }).join(' + ');
    }
  }

  function setFilter(active) {
    filterActive = active;
    var box = document.querySelector('[data-krbn-action="toggle-filter"]');
    if (box) box.checked = active;
    document.documentElement.classList.toggle('krbn-filter-active', active);
    processAllCards();
  }

  function renderFilterCount() {
    var count = document.querySelector('[data-krbn-bar-count]');
    if (!count) return;
    if (!filterActive) { count.hidden = true; return; }
    var visible = document.querySelectorAll('[data-krbn-fitments].krbn-compatible').length;
    count.textContent = visible === 0
      ? 'No compatible parts on this page'
      : visible + ' compatible ' + (visible === 1 ? 'part' : 'parts') + ' on this page';
    count.hidden = false;
  }

  /* ------------------------------------------------------------------ */
  /* Events                                                              */
  /* ------------------------------------------------------------------ */

  function onClick(event) {
    var t = event.target.closest('[data-krbn-action], [data-krbn-pick-make], [data-krbn-pick-family], [data-krbn-add], [data-krbn-remove], [data-krbn-garage-open]');
    if (!t) return;

    if (t.hasAttribute('data-krbn-garage-open')) { openDrawer(); return; }

    var action = t.getAttribute('data-krbn-action');
    if (action === 'open-garage') { openDrawer(); return; }
    if (action === 'open-picker') { openPicker(); return; }
    if (action === 'picker-back') { pickerBack(); return; }
    if (action === 'clear-garage') { clearGarage(); showView('garage'); return; }
    if (action === 'toggle-filter') { setFilter(t.checked); return; }

    if (t.hasAttribute('data-krbn-pick-make')) {
      picker.make = t.getAttribute('data-krbn-pick-make');
      var families = uniqueSorted(catalog
        .filter(function (v) { return v.make === picker.make; })
        .map(function (v) { return v.family; }));
      if (families.length === 1) {
        picker.family = families[0];
        picker.step = 'model';
      } else {
        picker.step = 'family';
      }
      renderPicker();
      return;
    }

    if (t.hasAttribute('data-krbn-pick-family')) {
      picker.family = t.getAttribute('data-krbn-pick-family');
      picker.step = 'model';
      renderPicker();
      return;
    }

    if (t.hasAttribute('data-krbn-add')) {
      addVehicle(t.getAttribute('data-krbn-add'));
      showView('garage');
      return;
    }

    if (t.hasAttribute('data-krbn-remove')) {
      removeVehicle(t.getAttribute('data-krbn-remove'));
    }
  }

  function renderAll() {
    renderHeaderCount();
    renderGarageView();
    renderCollectionBar();
    processAllCards();
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */

  function init() {
    loadCatalog();
    if (!catalog.length) return;

    document.addEventListener('click', onClick);
    document.addEventListener(EVT, renderAll);
    document.addEventListener('change', function (e) {
      if (e.target.matches && e.target.matches('[data-krbn-action="toggle-filter"]')) {
        setFilter(e.target.checked);
      }
    });

    renderAll();
    observeNewCards();

    // Minimal public API for future bundle integration
    window.KRBNGarage = {
      get: readGarage,
      add: addVehicle,
      remove: removeVehicle,
      clear: clearGarage
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
